import {
  applyAction,
  createDemoGameState,
  createMatch,
  getArmyDefinitionForInstance,
  getCardInstanceDefinition,
  getCommanderDefinitionForInstance,
  getLandDefinitionForInstance,
  getLegalActions,
  isSetupGameState,
  type CardDefinitionId,
  type CardInstanceId,
  type GameAction,
  type GameState,
  type GameStateV3,
  type LaneId,
  type MatchSetupInput,
  type PlayerId,
  type PlayerLaneState,
  type PlayerState,
} from "@fate-kingdom/engine";
import {
  ARMY_DEPLOYMENT_TEST_DECK,
  ARMY_DEPLOYMENT_TEST_DECK_CARD_IDS,
  ARMY_DEPLOYMENT_TEST_ENGINE_CATALOG,
  COMMANDER_DEPLOYMENT_TEST_DECK,
  COMMANDER_DEPLOYMENT_TEST_DECK_CARD_IDS,
  COMMANDER_DEPLOYMENT_TEST_ENGINE_CATALOG,
  TEST_DECKS,
  TEST_DECK_CARD_IDS,
  COMMAND_TEST_CARD_CATALOG,
  COMMAND_TEST_DECK,
  COMMAND_TEST_DECK_CARD_IDS,
  COMMAND_TEST_ENGINE_CATALOG,
  TEST_ENGINE_CATALOG,
  TEST_COMMANDER_DEFINITION_ID,
  TEST_COMMAND_ARMY_DEFINITION_IDS,
  TEST_COMMAND_CONSPIRACY_DEFINITION_ID,
  TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS,
  TEST_DEPLOYMENT_ARMY_DEFINITION_IDS,
  TEST_LAND_DEFINITION_IDS,
  TEST_SUPPLY_ARMY_DEFINITION_IDS,
} from "@fate-kingdom/testkit";

export type FoundationFixtureMode =
  | "setup-land"
  | "development"
  | "supply"
  | "command-disband"
  | "commander-deployment"
  | "army-deployment"
  | "cleanup"
  | "combat";

export const FOUNDATION_RULES_VERSION = "first-edition";

export function createFoundationSetupInput(seed = "foundation-land-0"): MatchSetupInput {
  return {
    matchId: `foundation-setup:${seed}`,
    seed,
    rulesVersion: FOUNDATION_RULES_VERSION,
    cardDataVersion: TEST_ENGINE_CATALOG.cardDataVersion,
    gameplayCatalogHash: TEST_ENGINE_CATALOG.gameplayCatalogHash,
    catalog: TEST_ENGINE_CATALOG,
    decks: {
      "player-1": {
        deckId: TEST_DECKS.player1.id,
        cardDefinitionIds: TEST_DECK_CARD_IDS.player1,
      },
      "player-2": {
        deckId: TEST_DECKS.player2.id,
        cardDefinitionIds: TEST_DECK_CARD_IDS.player2,
      },
    },
  };
}

function createSupplySetupInput(seed = "foundation-supply-2"): MatchSetupInput {
  return {
    ...createFoundationSetupInput(seed),
    decks: {
      "player-1": {
        deckId: TEST_DECKS.supply.id,
        cardDefinitionIds: TEST_DECK_CARD_IDS.supply,
      },
      "player-2": {
        deckId: TEST_DECKS.supply.id,
        cardDefinitionIds: TEST_DECK_CARD_IDS.supply,
      },
    },
  };
}

function createCommandSetupInput(seed = "foundation-command-disband-1"): MatchSetupInput {
  return {
    matchId: `foundation-command:${seed}`,
    seed,
    rulesVersion: FOUNDATION_RULES_VERSION,
    cardDataVersion: COMMAND_TEST_ENGINE_CATALOG.cardDataVersion,
    gameplayCatalogHash: COMMAND_TEST_ENGINE_CATALOG.gameplayCatalogHash,
    catalog: COMMAND_TEST_ENGINE_CATALOG,
    decks: {
      "player-1": {
        deckId: COMMAND_TEST_DECK.id,
        cardDefinitionIds: COMMAND_TEST_DECK_CARD_IDS,
      },
      "player-2": {
        deckId: COMMAND_TEST_DECK.id,
        cardDefinitionIds: COMMAND_TEST_DECK_CARD_IDS,
      },
    },
  };
}

function createCommanderDeploymentSetupInput(
  seed = "foundation-commander-deployment-2",
): MatchSetupInput {
  return {
    matchId: `foundation-commander-deployment:${seed}`,
    seed,
    rulesVersion: FOUNDATION_RULES_VERSION,
    cardDataVersion: COMMANDER_DEPLOYMENT_TEST_ENGINE_CATALOG.cardDataVersion,
    gameplayCatalogHash: COMMANDER_DEPLOYMENT_TEST_ENGINE_CATALOG.gameplayCatalogHash,
    catalog: COMMANDER_DEPLOYMENT_TEST_ENGINE_CATALOG,
    decks: {
      "player-1": {
        deckId: COMMANDER_DEPLOYMENT_TEST_DECK.id,
        cardDefinitionIds: COMMANDER_DEPLOYMENT_TEST_DECK_CARD_IDS,
      },
      "player-2": {
        deckId: COMMANDER_DEPLOYMENT_TEST_DECK.id,
        cardDefinitionIds: COMMANDER_DEPLOYMENT_TEST_DECK_CARD_IDS,
      },
    },
  };
}

function createArmyDeploymentSetupInput(seed = "foundation-army-deployment-2"): MatchSetupInput {
  return {
    matchId: `foundation-army-deployment:${seed}`,
    seed,
    rulesVersion: FOUNDATION_RULES_VERSION,
    cardDataVersion: ARMY_DEPLOYMENT_TEST_ENGINE_CATALOG.cardDataVersion,
    gameplayCatalogHash: ARMY_DEPLOYMENT_TEST_ENGINE_CATALOG.gameplayCatalogHash,
    catalog: ARMY_DEPLOYMENT_TEST_ENGINE_CATALOG,
    decks: {
      "player-1": {
        deckId: ARMY_DEPLOYMENT_TEST_DECK.id,
        cardDefinitionIds: ARMY_DEPLOYMENT_TEST_DECK_CARD_IDS,
      },
      "player-2": {
        deckId: ARMY_DEPLOYMENT_TEST_DECK.id,
        cardDefinitionIds: ARMY_DEPLOYMENT_TEST_DECK_CARD_IDS,
      },
    },
  };
}

function applyFixtureAction(state: GameState, action: GameAction): GameState {
  const result = applyAction(state, action, {
    rulesVersion: state.rulesVersion,
    cardDataVersion: state.cardDataVersion,
  });
  if (!result.ok) {
    throw new Error(
      `Foundation fixture action failed: ${result.failure.code} ${result.failure.message} ${JSON.stringify(result.failure.details)}`,
    );
  }
  return result.state;
}

function advanceFixture(state: GameState): GameState {
  return applyFixtureAction(state, {
    type: "advance-phase",
    playerId: state.activePlayerId,
  });
}

export function createSetupLandFixtureState(): GameStateV3 {
  const result = createMatch(createFoundationSetupInput());
  if (!result.ok) {
    throw new Error(`Foundation setup failed: ${result.failure.code}`);
  }

  const drawState = advanceFixture(result.state);
  const landState = advanceFixture(drawState);
  if (!isSetupGameState(landState) || landState.schemaVersion !== 3 || landState.phase !== "land") {
    throw new Error("Foundation setup fixture must enter the Land Phase.");
  }
  return landState;
}

/**
 * A deterministic developer checkpoint assembled exclusively through public
 * legal engine actions. It crosses deferred phases through the existing phase
 * scaffold and therefore is a focused Land proof, not a complete match loop.
 */
export function createDevelopmentFixtureState(): GameStateV3 {
  let state: GameState = createSetupLandFixtureState();
  const developmentPlayerId = state.activePlayerId;

  for (let step = 0; step < 160; step += 1) {
    if (state.activePlayerId === developmentPlayerId && state.phase === "land") {
      const legalActions = getLegalActions(state, developmentPlayerId);
      const activeLandCount = Object.values(state.lanes).filter(
        (lane) => lane.players[developmentPlayerId].status === "active",
      ).length;

      if (activeLandCount === 3) {
        const develop = legalActions.find((action) => action.type === "develop-land");
        if (develop !== undefined) {
          if (!isSetupGameState(state) || state.schemaVersion !== 3) {
            throw new Error("Expected schema-V3 setup state.");
          }
          return state;
        }
      } else {
        const emptyLaneId = (["left", "center", "right"] as const).find(
          (laneId) => state.lanes[laneId].players[developmentPlayerId].status === "empty",
        );
        const found = legalActions.find(
          (action) => action.type === "found-land" && action.laneId === emptyLaneId,
        );
        if (found !== undefined) {
          state = applyFixtureAction(state, found);
        }
      }
    }

    state = advanceFixture(state);
  }

  throw new Error("Unable to create the deterministic Land development fixture.");
}

function findOwnedInstance(
  state: Readonly<GameStateV3>,
  playerId: PlayerId,
  definitionId: string,
): string {
  const instance = Object.values(state.cardInstances).find(
    (candidate) =>
      candidate.ownerPlayerId === playerId && candidate.cardDefinitionId === definitionId,
  );
  if (instance === undefined) throw new Error(`Supply fixture requires ${definitionId}.`);
  return instance.cardInstanceId;
}

function removeFromPlayerZones(player: PlayerState, instanceIds: ReadonlySet<string>): PlayerState {
  const remove = (zone: readonly string[]) =>
    zone.filter((instanceId) => !instanceIds.has(instanceId));
  return {
    ...player,
    deck: remove(player.deck),
    hand: remove(player.hand),
    discard: remove(player.discard),
    exhaust: remove(player.exhaust),
    eventResolution: remove(player.eventResolution),
  };
}

/**
 * A focused developer state for mandatory Supply choice UI. Broad deployment
 * is intentionally deferred, so this fixture stations validated catalog
 * instances before entering Supply, then lets the public engine resolve every
 * production, Upkeep, discard and Kingdom Pool outcome.
 */
export function createSupplyChoiceFixtureState(): GameStateV3 {
  const created = createMatch(createSupplySetupInput());
  if (!created.ok) throw new Error(`Foundation Supply setup failed: ${created.failure.code}`);
  if (created.state.schemaVersion !== 3) {
    throw new Error("Foundation Supply fixture requires engine schema V3.");
  }

  const drawState = advanceFixture(created.state);
  const landState = advanceFixture(drawState);
  if (landState.schemaVersion !== 3 || landState.phase !== "land") {
    throw new Error("Foundation Supply fixture must reach the Land Phase before stationing cards.");
  }

  const playerId = landState.activePlayerId;
  const landCardInstanceId = findOwnedInstance(
    landState,
    playerId,
    TEST_LAND_DEFINITION_IDS.plains,
  );
  const spearmenCardInstanceId = findOwnedInstance(
    landState,
    playerId,
    TEST_SUPPLY_ARMY_DEFINITION_IDS.spearmen,
  );
  const cavalryCardInstanceId = findOwnedInstance(
    landState,
    playerId,
    TEST_SUPPLY_ARMY_DEFINITION_IDS.cavalry,
  );
  const landDefinition = getCardInstanceDefinition(landState, landCardInstanceId);
  const spearmenDefinition = getCardInstanceDefinition(landState, spearmenCardInstanceId);
  const cavalryDefinition = getCardInstanceDefinition(landState, cavalryCardInstanceId);
  if (
    landDefinition?.type !== "land" ||
    !("landTypeId" in landDefinition) ||
    spearmenDefinition?.type !== "army" ||
    !("upkeep" in spearmenDefinition) ||
    cavalryDefinition?.type !== "army" ||
    !("upkeep" in cavalryDefinition)
  ) {
    throw new Error("Foundation Supply fixture definitions must use catalog schema V2.");
  }

  const stage = landDefinition.stages[0];
  const movedInstances = new Set([
    landCardInstanceId,
    spearmenCardInstanceId,
    cavalryCardInstanceId,
  ]);
  const side = landState.lanes.right.players[playerId];
  const stagedState: GameStateV3 = {
    ...landState,
    players: {
      ...landState.players,
      [playerId]: removeFromPlayerZones(landState.players[playerId], movedInstances),
    },
    lanes: {
      ...landState.lanes,
      right: {
        ...landState.lanes.right,
        players: {
          ...landState.lanes.right.players,
          [playerId]: {
            ...side,
            status: "active",
            land: {
              cardInstanceIds: [landCardInstanceId],
              stage: 1,
              currentControl: stage.maximumControl,
              maximumControl: stage.maximumControl,
              attackBonus: 0,
              guardBonus: 0,
            },
            armies: [spearmenDefinition, cavalryDefinition].map((definition, index) => ({
              cardInstanceId: index === 0 ? spearmenCardInstanceId : cavalryCardInstanceId,
              armySize: definition.armySize,
              attack: definition.attack,
              guard: definition.guard,
            })),
          },
        },
      },
    },
  };

  const supplyState = advanceFixture(stagedState);
  if (
    supplyState.schemaVersion !== 3 ||
    supplyState.phase !== "supply" ||
    supplyState.supplyStatus.type !== "awaiting-upkeep-discard"
  ) {
    throw new Error("Foundation Supply fixture must await an authoritative Upkeep discard.");
  }
  return supplyState;
}

function findUnoccupiedOwnedInstance(
  state: Readonly<GameStateV3>,
  playerId: PlayerId,
  definitionId: string,
  occupied: ReadonlySet<string>,
): string {
  const instance = Object.values(state.cardInstances)
    .filter(
      (candidate) =>
        candidate.ownerPlayerId === playerId &&
        candidate.cardDefinitionId === definitionId &&
        !occupied.has(candidate.cardInstanceId),
    )
    .sort((left, right) => left.originalDeckOrdinal - right.originalDeckOrdinal)[0];
  if (instance === undefined) throw new Error(`Command fixture requires ${definitionId}.`);
  return instance.cardInstanceId;
}

function takeOwnedInstances(
  state: Readonly<GameStateV3>,
  playerId: PlayerId,
  definitionId: CardDefinitionId,
  count: number,
  occupied: Set<CardInstanceId>,
): readonly CardInstanceId[] {
  const instances = Object.values(state.cardInstances)
    .filter(
      (candidate) =>
        candidate.ownerPlayerId === playerId &&
        candidate.cardDefinitionId === definitionId &&
        !occupied.has(candidate.cardInstanceId),
    )
    .sort((left, right) => left.originalDeckOrdinal - right.originalDeckOrdinal)
    .slice(0, count)
    .map((candidate) => candidate.cardInstanceId);
  if (instances.length !== count) {
    throw new Error(
      `Commander Deployment fixture requires ${String(count)} ${definitionId} instance(s).`,
    );
  }
  instances.forEach((cardInstanceId) => occupied.add(cardInstanceId));
  return instances;
}

interface CommanderDeploymentLaneInput {
  laneId: LaneId;
  landDefinitionId: CardDefinitionId;
  landStage: 1 | 2 | 3;
  commanderDefinitionId?: CardDefinitionId;
  armyDefinitionIds?: readonly CardDefinitionId[];
  conspiracy?: boolean;
}

function createCommanderDeploymentLaneSide(
  state: Readonly<GameStateV3>,
  playerId: PlayerId,
  input: CommanderDeploymentLaneInput,
  occupied: Set<CardInstanceId>,
): PlayerLaneState {
  const landCardInstanceIds = takeOwnedInstances(
    state,
    playerId,
    input.landDefinitionId,
    input.landStage,
    occupied,
  );
  const firstLandCardInstanceId = landCardInstanceIds[0];
  if (firstLandCardInstanceId === undefined) {
    throw new Error("Commander Deployment fixture requires a Land instance.");
  }
  const landDefinition = getLandDefinitionForInstance(state, firstLandCardInstanceId);
  if (landDefinition === undefined || !("landTypeId" in landDefinition)) {
    throw new Error("Commander Deployment fixture requires a current Land definition.");
  }
  const stage = landDefinition.stages[input.landStage - 1];
  if (stage === undefined) throw new Error("Commander Deployment fixture Land Stage is missing.");

  const commanderCardInstanceId =
    input.commanderDefinitionId === undefined
      ? undefined
      : takeOwnedInstances(state, playerId, input.commanderDefinitionId, 1, occupied)[0];
  const commanderDefinition =
    commanderCardInstanceId === undefined
      ? undefined
      : getCommanderDefinitionForInstance(state, commanderCardInstanceId);
  if (commanderCardInstanceId !== undefined && commanderDefinition === undefined) {
    throw new Error("Commander Deployment fixture requires a schema-3 Commander definition.");
  }

  const armies = (input.armyDefinitionIds ?? []).map((definitionId) => {
    const cardInstanceId = takeOwnedInstances(state, playerId, definitionId, 1, occupied)[0];
    if (cardInstanceId === undefined) throw new Error(`Missing fixture Army ${definitionId}.`);
    const definition = getArmyDefinitionForInstance(state, cardInstanceId);
    if (definition === undefined) throw new Error(`${definitionId} must resolve as an Army.`);
    return {
      cardInstanceId,
      armySize: definition.armySize,
      attack: definition.attack,
      guard: definition.guard,
    };
  });
  const conspiracyCardInstanceId =
    input.conspiracy === true
      ? (takeOwnedInstances(
          state,
          playerId,
          TEST_COMMAND_CONSPIRACY_DEFINITION_ID,
          1,
          occupied,
        )[0] ?? null)
      : null;

  return {
    status: "active",
    land: {
      cardInstanceIds: landCardInstanceIds,
      stage: input.landStage,
      currentControl: stage.maximumControl,
      maximumControl: stage.maximumControl,
      attackBonus: 0,
      guardBonus: 0,
    },
    commander:
      commanderCardInstanceId === undefined || commanderDefinition === undefined
        ? null
        : {
            cardInstanceId: commanderCardInstanceId,
            attack: commanderDefinition.attack,
            guard: commanderDefinition.guard,
            armyCapacity: commanderDefinition.armyCapacity,
            readiness: "ready",
            deployedThisTurn: false,
            mayAttackOnDeploymentTurn: false,
          },
    armies,
    conspiracyCardInstanceId,
    attachedCardInstanceIds: [],
    combatModifiers: [],
  };
}

function createEmptyLaneSide(): PlayerLaneState {
  return {
    status: "empty",
    land: null,
    commander: null,
    armies: [],
    conspiracyCardInstanceId: null,
    attachedCardInstanceIds: [],
    combatModifiers: [],
  };
}

/**
 * Supplied-state browser checkpoint for the first authoritative Commander
 * deployment UI. Army garrisons remain explicitly staged because Army
 * Deployment is deferred; real Supply then produces the Pool and the engine
 * advances normally into Command.
 */
export function createCommanderDeploymentFixtureState(): GameStateV3 {
  const created = createMatch(createCommanderDeploymentSetupInput());
  if (!created.ok || created.state.schemaVersion !== 3) {
    throw new Error(
      created.ok ? "Deployment fixture requires schema V3." : created.failure.message,
    );
  }
  const drawState = advanceFixture(created.state);
  const landState = advanceFixture(drawState);
  if (
    landState.schemaVersion !== 3 ||
    landState.catalog.schemaVersion !== 3 ||
    landState.phase !== "land" ||
    landState.activePlayerId !== "player-1"
  ) {
    throw new Error("Deployment fixture must reach schema-3 Land as player 1.");
  }

  const playerId = landState.activePlayerId;
  const opponentPlayerId: PlayerId = "player-2";
  const playerOccupied = new Set<CardInstanceId>();
  const opponentOccupied = new Set<CardInstanceId>();
  const laneInputs: readonly CommanderDeploymentLaneInput[] = [
    {
      laneId: "left",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.plains,
      landStage: 1,
    },
    {
      laneId: "center",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.river,
      landStage: 1,
      armyDefinitionIds: [
        TEST_COMMAND_ARMY_DEFINITION_IDS.vanguard,
        TEST_COMMAND_ARMY_DEFINITION_IDS.sentries,
      ],
    },
    {
      laneId: "right",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.mountain,
      landStage: 2,
      commanderDefinitionId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.balanced,
      armyDefinitionIds: [TEST_COMMAND_ARMY_DEFINITION_IDS.scouts],
      conspiracy: true,
    },
  ];
  const lanes = { ...landState.lanes };
  for (const input of laneInputs) {
    const playerSide = createCommanderDeploymentLaneSide(
      landState,
      playerId,
      input,
      playerOccupied,
    );
    const opponentLandCardInstanceId = takeOwnedInstances(
      landState,
      opponentPlayerId,
      input.landDefinitionId,
      1,
      opponentOccupied,
    )[0];
    if (opponentLandCardInstanceId === undefined) {
      throw new Error("Deployment fixture requires an opposing Land.");
    }
    const opponentLand = getLandDefinitionForInstance(landState, opponentLandCardInstanceId);
    if (opponentLand === undefined) throw new Error("Opposing Land definition is missing.");
    const opponentSide: PlayerLaneState = {
      ...createEmptyLaneSide(),
      status: "active",
      land: {
        cardInstanceIds: [opponentLandCardInstanceId],
        stage: 1,
        currentControl: opponentLand.stages[0].maximumControl,
        maximumControl: opponentLand.stages[0].maximumControl,
        attackBonus: 0,
        guardBonus: 0,
      },
    };
    const lane = landState.lanes[input.laneId];
    lanes[input.laneId] = {
      ...lane,
      players: {
        ...lane.players,
        [playerId]: playerSide,
        [opponentPlayerId]: opponentSide,
      },
    };
  }

  const incomingCommanderCardInstanceIds = Object.values(
    TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS,
  ).map(
    (definitionId) => takeOwnedInstances(landState, playerId, definitionId, 1, playerOccupied)[0]!,
  );
  const player = removeFromPlayerZones(landState.players[playerId], playerOccupied);
  const opponent = removeFromPlayerZones(landState.players[opponentPlayerId], opponentOccupied);
  const staged: GameStateV3 = {
    ...landState,
    players: {
      ...landState.players,
      [playerId]: {
        ...player,
        hand: [...player.hand, ...incomingCommanderCardInstanceIds],
        actionsUsed: {
          ...player.actionsUsed,
          landActions: 1,
          deploymentActions: 0,
          eventsPlayed: 1,
          eventAuthorizationLanesUsed: ["right"],
        },
      },
      [opponentPlayerId]: opponent,
    },
    lanes,
    supplyStatus: { type: "not-started" },
  };
  const supplied = advanceFixture(staged);
  if (
    supplied.schemaVersion !== 3 ||
    supplied.phase !== "supply" ||
    supplied.supplyStatus.type !== "resolved"
  ) {
    throw new Error("Deployment fixture requires real, resolved Supply.");
  }
  const command = advanceFixture(supplied);
  if (
    command.schemaVersion !== 3 ||
    command.catalog.schemaVersion !== 3 ||
    command.phase !== "command" ||
    command.supplyStatus.type !== "not-started"
  ) {
    throw new Error("Deployment fixture must enter schema-3 Command.");
  }
  return command;
}

/**
 * Supplied-state browser checkpoint for Army deployment. Lands, Commanders
 * and existing zero-Upkeep garrisons are staged at this explicit fixture
 * boundary; authoritative Supply then produces the Pool and advances normally
 * into Command. Incoming Armies remain real owned instances in hand.
 */
export function createArmyDeploymentFixtureState(): GameStateV3 {
  const created = createMatch(createArmyDeploymentSetupInput());
  if (!created.ok || created.state.schemaVersion !== 3) {
    throw new Error(
      created.ok ? "Army Deployment fixture requires schema V3." : created.failure.message,
    );
  }
  const drawState = advanceFixture(created.state);
  const landState = advanceFixture(drawState);
  if (
    landState.schemaVersion !== 3 ||
    landState.catalog.schemaVersion !== 3 ||
    landState.phase !== "land" ||
    landState.activePlayerId !== "player-1"
  ) {
    throw new Error("Army Deployment fixture must reach schema-3 Land as player 1.");
  }

  const playerId = landState.activePlayerId;
  const opponentPlayerId: PlayerId = "player-2";
  const playerOccupied = new Set<CardInstanceId>();
  const opponentOccupied = new Set<CardInstanceId>();
  const laneInputs: readonly CommanderDeploymentLaneInput[] = [
    {
      laneId: "left",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.plains,
      landStage: 3,
      commanderDefinitionId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.highCapacity,
      armyDefinitionIds: [
        TEST_COMMAND_ARMY_DEFINITION_IDS.vanguard,
        TEST_COMMAND_ARMY_DEFINITION_IDS.vanguard,
        TEST_COMMAND_ARMY_DEFINITION_IDS.sentries,
        TEST_COMMAND_ARMY_DEFINITION_IDS.wardens,
      ],
    },
    {
      laneId: "center",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.river,
      landStage: 3,
      commanderDefinitionId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.balanced,
      armyDefinitionIds: [TEST_COMMAND_ARMY_DEFINITION_IDS.sentries],
    },
    {
      laneId: "right",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.mountain,
      landStage: 2,
    },
  ];
  const lanes = { ...landState.lanes };
  for (const input of laneInputs) {
    const playerSide = createCommanderDeploymentLaneSide(
      landState,
      playerId,
      input,
      playerOccupied,
    );
    const opponentLandCardInstanceId = takeOwnedInstances(
      landState,
      opponentPlayerId,
      input.landDefinitionId,
      1,
      opponentOccupied,
    )[0];
    if (opponentLandCardInstanceId === undefined) {
      throw new Error("Army Deployment fixture requires an opposing Land.");
    }
    const opponentLand = getLandDefinitionForInstance(landState, opponentLandCardInstanceId);
    if (opponentLand === undefined) {
      throw new Error("Army Deployment fixture opposing Land definition is missing.");
    }
    const opponentSide: PlayerLaneState = {
      ...createEmptyLaneSide(),
      status: "active",
      land: {
        cardInstanceIds: [opponentLandCardInstanceId],
        stage: 1,
        currentControl: opponentLand.stages[0].maximumControl,
        maximumControl: opponentLand.stages[0].maximumControl,
        attackBonus: 0,
        guardBonus: 0,
      },
    };
    const lane = landState.lanes[input.laneId];
    lanes[input.laneId] = {
      ...lane,
      players: {
        ...lane.players,
        [playerId]: playerSide,
        [opponentPlayerId]: opponentSide,
      },
    };
  }

  const incomingArmyCardInstanceIds = Object.values(TEST_DEPLOYMENT_ARMY_DEFINITION_IDS).map(
    (definitionId) => takeOwnedInstances(landState, playerId, definitionId, 1, playerOccupied)[0]!,
  );
  const player = removeFromPlayerZones(landState.players[playerId], playerOccupied);
  const opponent = removeFromPlayerZones(landState.players[opponentPlayerId], opponentOccupied);
  const staged: GameStateV3 = {
    ...landState,
    players: {
      ...landState.players,
      [playerId]: {
        ...player,
        deck: [...player.deck, ...player.hand],
        hand: incomingArmyCardInstanceIds,
        actionsUsed: {
          ...player.actionsUsed,
          landActions: 1,
          deploymentActions: 0,
          eventsPlayed: 0,
          eventAuthorizationLanesUsed: [],
        },
      },
      [opponentPlayerId]: opponent,
    },
    lanes,
    supplyStatus: { type: "not-started" },
  };
  const supplied = advanceFixture(staged);
  if (
    supplied.schemaVersion !== 3 ||
    supplied.phase !== "supply" ||
    supplied.supplyStatus.type !== "resolved"
  ) {
    throw new Error("Army Deployment fixture requires real, resolved Supply.");
  }
  const command = advanceFixture(supplied);
  if (
    command.schemaVersion !== 3 ||
    command.catalog.schemaVersion !== 3 ||
    command.phase !== "command" ||
    command.supplyStatus.type !== "not-started"
  ) {
    throw new Error("Army Deployment fixture must enter schema-3 Command.");
  }
  return command;
}

/** A supplied pre-Cleanup checkpoint for the engine-driven handoff UI. */
export function createCleanupFixtureState(): GameStateV3 {
  const command = createArmyDeploymentFixtureState();
  return {
    ...command,
    phase: "cleanup",
    supplyStatus: { type: "not-started" },
  };
}

/**
 * An honestly named supplied-state Command checkpoint. Authoritative
 * Deployment is deferred, so card instances are stationed in this fixture
 * boundary. The public engine then performs real Supply entry, resolves the
 * Pool and advances into Command, including its not-started Supply marker.
 */
export function createSuppliedCommandDisbandFixtureState(): GameStateV3 {
  const created = createMatch(createCommandSetupInput());
  if (!created.ok || created.state.schemaVersion !== 3) {
    throw new Error(created.ok ? "Command fixture requires schema V3." : created.failure.message);
  }

  const drawState = advanceFixture(created.state);
  const landState = advanceFixture(drawState);
  if (landState.schemaVersion !== 3 || landState.phase !== "land") {
    throw new Error("Command fixture must reach Land before its supplied-state boundary.");
  }

  const commanderDefinition = COMMAND_TEST_CARD_CATALOG.cards.find(
    (definition) => definition.id === TEST_COMMANDER_DEFINITION_ID,
  );
  if (commanderDefinition?.type !== "commander") {
    throw new Error("Command fixture requires its test-only Commander definition.");
  }

  const playerId = landState.activePlayerId;
  const occupied = new Set<string>();
  const lanes = { ...landState.lanes };
  const laneInputs: readonly {
    laneId: LaneId;
    landDefinitionId: string;
    armyDefinitionIds: readonly string[];
  }[] = [
    {
      laneId: "left",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.plains,
      armyDefinitionIds: [
        TEST_COMMAND_ARMY_DEFINITION_IDS.vanguard,
        TEST_COMMAND_ARMY_DEFINITION_IDS.sentries,
      ],
    },
    {
      laneId: "center",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.mountain,
      armyDefinitionIds: [TEST_COMMAND_ARMY_DEFINITION_IDS.outriders],
    },
    {
      laneId: "right",
      landDefinitionId: TEST_LAND_DEFINITION_IDS.river,
      armyDefinitionIds: [
        TEST_COMMAND_ARMY_DEFINITION_IDS.wardens,
        TEST_COMMAND_ARMY_DEFINITION_IDS.scouts,
      ],
    },
  ];

  for (const input of laneInputs) {
    const landCardInstanceId = findUnoccupiedOwnedInstance(
      landState,
      playerId,
      input.landDefinitionId,
      occupied,
    );
    occupied.add(landCardInstanceId);
    const landDefinition = getCardInstanceDefinition(landState, landCardInstanceId);
    if (landDefinition?.type !== "land") {
      throw new Error("Command fixture requires schema-V2 Land definitions.");
    }

    const commanderCardInstanceId = findUnoccupiedOwnedInstance(
      landState,
      playerId,
      TEST_COMMANDER_DEFINITION_ID,
      occupied,
    );
    occupied.add(commanderCardInstanceId);
    const armies = input.armyDefinitionIds.map((definitionId) => {
      const cardInstanceId = findUnoccupiedOwnedInstance(
        landState,
        playerId,
        definitionId,
        occupied,
      );
      occupied.add(cardInstanceId);
      const definition = getArmyDefinitionForInstance(landState, cardInstanceId);
      if (definition === undefined) throw new Error(`${definitionId} must resolve as an Army.`);
      return {
        cardInstanceId,
        armySize: definition.armySize,
        attack: definition.attack,
        guard: definition.guard,
      };
    });
    const stage = landDefinition.stages[0];
    const lane = landState.lanes[input.laneId];

    lanes[input.laneId] = {
      ...lane,
      players: {
        ...lane.players,
        [playerId]: {
          status: "active",
          land: {
            cardInstanceIds: [landCardInstanceId],
            stage: 1,
            currentControl: stage.maximumControl,
            maximumControl: stage.maximumControl,
            attackBonus: 0,
            guardBonus: 0,
          },
          commander: {
            cardInstanceId: commanderCardInstanceId,
            attack: commanderDefinition.stats.attack,
            guard: commanderDefinition.stats.guard,
            armyCapacity: commanderDefinition.stats.armyCapacity,
            readiness: "ready",
            deployedThisTurn: false,
            mayAttackOnDeploymentTurn: false,
          },
          armies,
          conspiracyCardInstanceId: null,
          attachedCardInstanceIds: [],
          combatModifiers: [],
        },
      },
    };
  }

  const player = removeFromPlayerZones(landState.players[playerId], occupied);
  const staged: GameStateV3 = {
    ...landState,
    players: {
      ...landState.players,
      [playerId]: {
        ...player,
        actionsUsed: { ...player.actionsUsed, landActions: 1, deploymentActions: 1 },
      },
    },
    lanes,
    supplyStatus: { type: "not-started" },
  };
  const supplied = advanceFixture(staged);
  if (
    supplied.schemaVersion !== 3 ||
    supplied.phase !== "supply" ||
    supplied.supplyStatus.type !== "resolved"
  ) {
    throw new Error("Command fixture requires real, fully resolved Supply.");
  }

  const command = advanceFixture(supplied);
  if (
    command.schemaVersion !== 3 ||
    command.phase !== "command" ||
    command.supplyStatus.type !== "not-started"
  ) {
    throw new Error("Command fixture must enter Command with its reset Supply marker.");
  }
  const poolTotal = Object.values(command.players[playerId].kingdomPool).reduce(
    (total, amount) => total + amount,
    0,
  );
  if (poolTotal === 0) throw new Error("Command fixture requires a nonempty Kingdom Pool.");
  return command;
}

export function createFoundationFixtureState(mode: FoundationFixtureMode): GameState {
  if (mode === "combat") return createDemoGameState();
  if (mode === "cleanup") return createCleanupFixtureState();
  if (mode === "army-deployment") return createArmyDeploymentFixtureState();
  if (mode === "commander-deployment") return createCommanderDeploymentFixtureState();
  if (mode === "command-disband") return createSuppliedCommandDisbandFixtureState();
  if (mode === "supply") return createSupplyChoiceFixtureState();
  if (mode === "development") return createDevelopmentFixtureState();
  return createSetupLandFixtureState();
}
